import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import { Input, type InputProps } from "@/components/podkit/forms/Input";
import { cn } from "@/components/podkit/lib/cn";
import IconMagnifyingGlass from "central-icons/IconMagnifyingGlass";
import { forwardRef } from "react";

export type SearchInputProps = Omit<InputProps, "prefix" | "suffix" | "type"> & {
    /** Callback when the clear button is clicked */
    onClear?: () => void;
    /** Additional classes for the wrapper div */
    wrapperClassName?: string;
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
    ({ className, wrapperClassName, onClear, value, onChange, disabled, ...props }, ref) => {
        const hasValue = value !== undefined && value !== "";
        const showClearButton = hasValue && !disabled && onClear;

        return (
            <div className={cn("relative [&>div]:max-w-none", disabled && "opacity-50", wrapperClassName)}>
                <Input
                    ref={ref}
                    type="text"
                    prefix={<IconMagnifyingGlass className="size-4" />}
                    className={cn(showClearButton && "pr-10", "max-w-none transition-all duration-150 ease-out", className)}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    {...props}
                />
                {showClearButton && (
                    <button
                        type="button"
                        onClick={onClear}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full text-content-secondary hover:text-content-primary"
                        aria-label="Clear search"
                        data-tracking-id="search-input-clear"
                    >
                        <IconCircleX className="size-4" />
                    </button>
                )}
            </div>
        );
    },
);
SearchInput.displayName = "SearchInput";
